<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่ *</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <b-button variant="primary" class="btn-primary-lg" :disabled="!dateFrom || !dateTo" @click="load">
          แสดงข้อมูล
        </b-button>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

        <!--
          The mismatch flag is the entire reason this log exists — tier
          vouchers are deliberately bearer (no patient binding), and a
          mismatched row is the ONLY signal anyone gets that a code was
          transferred or sold (see the header comment on
          tier_repo/getTierUpLog.js). It must be impossible to miss while
          skimming this table: a summary banner above the table when any row
          in the loaded range is mismatched, a whole-row danger style, AND a
          spelled-out warning in its own column naming both the patient the
          code was issued to and who actually redeemed it — not a lone icon.
        -->
        <b-alert :show="mismatchCount > 0" variant="danger" class="mismatch-summary">
          <i class="fas fa-exclamation-triangle mr-2" aria-hidden="true"></i>
          พบ {{ mismatchCount }} รายการที่บัตรกำนัลถูกใช้โดยคนไข้คนละคนกับผู้ได้รับสิทธิ์ในช่วงเวลานี้ —
          ตรวจสอบด้านล่าง
        </b-alert>

        <div class="toolbar d-flex align-items-center flex-wrap mb-2">
          <div class="spacer flex-grow-1"></div>
          <b-dropdown text="ส่งออก" size="sm" variant="outline-success" right :disabled="!report.length">
            <b-dropdown-item @click="exportExcel">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="exportPdf">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <b-table
          id="tierUpLogTable"
          :items="report"
          :fields="fields"
          :busy="isLoading"
          :tbody-tr-class="rowClass"
          responsive
          hover
          show-empty
          empty-text="ไม่มีข้อมูลให้แสดงในช่วงวันที่นี้"
        >
          <template #cell(eventType)="{ value }">{{ eventTypeLabel(value) }}</template>
          <template #cell(fromTierName)="{ value }">{{ value || "—" }}</template>
          <template #cell(toTierName)="{ value }">{{ value || "—" }}</template>
          <template #cell(qualifiedAmount)="{ value }">{{ formatBaht(value) }}</template>
          <template #cell(voucherCode)="{ value }">{{ value || "—" }}</template>
          <template #cell(creationDt)="{ value }">{{ fmtDate(value) }}</template>
          <template #cell(mismatch)="{ item }">
            <div v-if="item.mismatch" class="mismatch-cell">
              <i class="fas fa-exclamation-triangle mr-1" aria-hidden="true"></i>
              <strong>{{ mismatchWarning(item) }}</strong>
            </div>
            <span v-else-if="item.voucherCode" class="text-muted">ใช้ตรงกับคนไข้ที่ได้รับสิทธิ์</span>
            <span v-else class="text-muted">—</span>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { tierErrorMessage } from "@/utils/tierErrors";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { tierEventTypeLabel } from "@/utils/tierDisplay";
import { tierUpMismatchWarning, tierUpRowVariant } from "@/utils/tierReportDisplay";

export default {
  name: "TierUpLogReport",
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      report: [],
      dateRange: [],
      reportTitle: "รายงานประวัติการเปลี่ยนระดับ",
      reportNameEn: "TierUpLog",
      fields: [
        { key: "patientName", label: "คนไข้ที่ได้รับสิทธิ์" },
        { key: "eventType", label: "ประเภทเหตุการณ์" },
        { key: "fromTierName", label: "จากระดับ" },
        { key: "toTierName", label: "ถึงระดับ" },
        { key: "qualifiedAmount", label: "ยอดสะสม ณ ขณะนั้น (บาท)", class: "text-right" },
        { key: "voucherCode", label: "รหัสบัตรกำนัล" },
        { key: "mismatch", label: "การใช้รหัสบัตร" },
        { key: "creationDt", label: "วันที่/เวลา" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    dateFrom() {
      return this.dateRange[0] ? moment(this.dateRange[0]).startOf("day").toISOString(true) : "";
    },
    dateTo() {
      return this.dateRange[1] ? moment(this.dateRange[1]).endOf("day").toISOString(true) : "";
    },
    mismatchCount() {
      return this.report.filter(r => r.mismatch).length;
    },
  },
  methods: {
    ...mapActions("moduleTier", ["getTierReport"]),

    formatBaht(value) {
      return Number(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // DD/MM/YYYY, not moment's "th" locale — see TierRosterReport.vue /
    // PatientTierPanel.vue for why: the "th" locale's year is plain
    // Gregorian despite reading like Buddhist Era.
    fmtDate(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
    },
    eventTypeLabel(type) {
      return tierEventTypeLabel(type);
    },
    mismatchWarning(row) {
      return tierUpMismatchWarning(row);
    },
    rowClass(item) {
      return tierUpRowVariant(item) ? "table-danger mismatch-row" : null;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },

    async load() {
      if (!this.clinicUrl || !this.dateFrom || !this.dateTo) return;
      this.isLoading = true;
      this.errorMessage = "";
      try {
        this.report = await this.getTierReport({
          clinicUrl: this.clinicUrl,
          type: "tierUp",
          startDt: this.dateFrom,
          endDt: this.dateTo,
        });
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "โหลดรายงานไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },

    exportOptions() {
      return {
        tableId: "tierUpLogTable",
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    exportExcel() {
      exportTableToExcel(this.exportOptions());
    },
    exportPdf() {
      exportReportToPdf(this.exportOptions());
    },
  },
  mounted() {
    // Default to the trailing 30 days — tier-change events are infrequent
    // enough that "today" (the default several other reports in this repo
    // use, e.g. ClinicReceiptIncomeReport.vue) would routinely load an
    // empty table.
    const today = moment().startOf("day").toDate();
    const from = moment().subtract(29, "days").startOf("day").toDate();
    this.dateRange = [from, today];
    this.load();
  },
};
</script>

<style scoped>
.spacer {
  min-width: 8px;
}
.mismatch-summary {
  font-weight: 600;
}
.mismatch-cell {
  color: #a71d2a;
}
</style>
